/**
 * Labstep
 *
 * @module prosemirror/extensions/commands/plugin
 * @desc Commands plugin definition to store the commands menu state
 */

import { SLASH_TRIGGER } from 'labstep-web/constants/prosemirror';
import {
  getHeader,
  MENU_COMMANDS_ELEMENTS,
} from 'labstep-web/prosemirror/components/Menu/Commands/elements';
import type { IMenuCommandsElementProps } from 'labstep-web/prosemirror/components/Menu/Commands/types';
import {
  ENTER_SUBMENU,
  EXIT_SUBMENU,
  SELECT_NEXT,
  SELECT_PREVIOUS,
  SET_ELEMENTS,
  SUBMIT_ELEMENT,
} from 'labstep-web/prosemirror/extensions/slash/commands/actions';
import type { IProseMirrorCommandsElement } from 'labstep-web/prosemirror/extensions/slash/types';
import { sortElementsBySection } from 'labstep-web/prosemirror/extensions/slash/utils/sort-elements';
import { getToken } from 'labstep-web/prosemirror/utils/selection';
import { Plugin, PluginKey } from 'prosemirror-state';
import type { IProseMirrorReferencingToken } from '../../referencing/types';
import { SET_IS_OPEN } from '../commands/actions';
import type { PluginState } from './types';

const KEY = 'commands';

export const commandsPluginKey = new PluginKey(KEY);

export const setAvailableElements = (
  elements: IProseMirrorCommandsElement[],
  token: IProseMirrorReferencingToken,
) => {
  return elements.filter((element) => {
    const menuCommandsElement = MENU_COMMANDS_ELEMENTS.find(
      (e) => element === e.id,
    ) as IMenuCommandsElementProps;
    const search = token.text.toLowerCase();
    const headerMatch = getHeader(menuCommandsElement)
      .toLowerCase()
      .includes(search);
    const keywordMatch = menuCommandsElement.keywords?.some((k) =>
      k.toLowerCase().includes(search),
    );
    return headerMatch || keywordMatch;
  });
};

export const updateState = (
  value: PluginState,
  action: any,
  token?: IProseMirrorReferencingToken | null,
) => {
  if (action.type === SET_ELEMENTS) {
    return {
      ...value,
      elements: action.elements,
      availableElements: token
        ? setAvailableElements(action.elements, token)
        : action.elements,
    };
  }
  if (action.type === SELECT_PREVIOUS) {
    const currentElements =
      value.submenuElements || value.availableElements;
    // Use sorted elements for navigation bounds to match UI order
    const sortedElements = sortElementsBySection(currentElements);
    return {
      ...value,
      index: value.index > 0 ? value.index - 1 : value.index,
    };
  }
  if (action.type === SELECT_NEXT) {
    const currentElements =
      value.submenuElements || value.availableElements;
    // Use sorted elements for navigation bounds to match UI order
    const sortedElements = sortElementsBySection(currentElements);
    return {
      ...value,
      index:
        value.index < sortedElements.length - 1
          ? value.index + 1
          : value.index,
    };
  }
  if (action.type === SUBMIT_ELEMENT) {
    return {
      ...value,
      submittedElement: action.element,
    };
  }
  if (action.type === ENTER_SUBMENU) {
    return {
      ...value,
      submenuParent: action.parent,
      submenuElements: action.submenuElements,
      index: 0,
    };
  }
  if (action.type === EXIT_SUBMENU) {
    return {
      ...value,
      submenuParent: undefined,
      submenuElements: undefined,
      index: 0,
    };
  }
  if (action.type === SET_IS_OPEN && action.isOpen === false) {
    return initialState;
  }
  if (action.type === SET_IS_OPEN && action.isOpen === true) {
    return { ...value, isOpen: true };
  }
  return value;
};

export const initialState: PluginState = {
  isOpen: false,
  elements: [],
  availableElements: [],
  index: 0,
  submittedElement: undefined,
  submenuParent: undefined,
  submenuElements: undefined,
};

const commandsPlugin = new Plugin({
  key: commandsPluginKey,
  state: {
    init: () => {
      return initialState;
    },
    apply: (tr, value, oldState, newState) => {
      const action = tr.getMeta(commandsPlugin);
      const token = getToken(newState, SLASH_TRIGGER);

      if (action) {
        return updateState(value, action, token);
      }

      if (!token) {
        return initialState;
      }

      // Only filter elements if we're not in a submenu
      if (value.submenuElements) {
        return value; // Keep current state including submenu
      }

      const availableElements = setAvailableElements(
        value.elements,
        token,
      );

      return {
        ...value,
        availableElements,
      };
    },
  },
});

export default commandsPlugin;
